import { env } from '@/config/env';
import { API } from '@/config/api';
import { apiClient } from '@/shared/api/apiClient';

/** What the OAuth token endpoint returns on a password grant. */
interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
}

export interface SignInRequest {
  email: string;
  password: string;
}

export interface Session {
  accessToken: string;
  refreshToken: string | undefined;
  /** Seconds until the access token stops working. */
  expiresIn: number;
}

/**
 * Exchanges an email and password for a session.
 *
 * <h2>Form-encoded, not JSON</h2>
 *
 * RFC 6749 defines the token endpoint's request as
 * `application/x-www-form-urlencoded`, and Spring Authorization Server enforces
 * it. A JSON body is refused with a message that does not say why.
 *
 * <h2>The client id travels in the body, and there is no secret</h2>
 *
 * This client is registered with `none` as its authentication method — it is a
 * browser, and a browser cannot keep a secret. Anything shipped as one is
 * readable by anybody who opens the bundle, so the backend does not ask for one;
 * it requires PKCE on the authorization-code flow instead.
 *
 * <h2>No `Authorization` header on this call</h2>
 *
 * Handled by the request interceptor, which skips this endpoint entirely. Setting
 * the header to an empty string here does NOT do the same thing and was a real
 * bug: an empty header is still sent, Spring finds a credential it cannot parse,
 * and answers 401 -- rejecting the application rather than judging the password,
 * which then reads as wrong credentials and is not.
 */
export async function signIn(request: SignInRequest): Promise<Session> {
  const body = new URLSearchParams({
    grant_type: 'password',
    client_id: env.oauthClientId,
    username: request.email,
    password: request.password,
  });

  const token = await apiClient.post<TokenResponse>(API.token, body, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });

  return {
    accessToken: token.access_token,
    refreshToken: token.refresh_token,
    expiresIn: token.expires_in,
  };
}
